import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import {
  drugLicenseService,
  CreateDrugLicenseData,
} from "../../services/drug-license.service";
import { showToast } from "../../lib/toast";
import { useAuthStore } from "../../stores/authStore";

const LICENSE_TYPES = [
  "RETAIL",
  "WHOLESALE",
  "RETAIL_WHOLESALE",
  "MANUFACTURING",
  "IMPORT",
  "EXPORT",
];

const SCHEDULES = [
  "SCHEDULE_G",
  "SCHEDULE_H",
  "SCHEDULE_H1",
  "SCHEDULE_X",
  "NDPS",
  "CONTROLLED",
];

export function CreateDrugLicensePage() {
  const navigate = useNavigate();
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateDrugLicenseData>({
    storeId: "",
    licenseNumber: "",
    licenseType: "RETAIL",
    issuingAuthority: "",
    issueDate: "",
    expiryDate: "",
    registeredPharmacist: "",
    pharmacistRegNumber: "",
    schedulesCovered: [],
    documentUrl: "",
    notes: "",
  });

  useEffect(() => {
    setFormData((previous) => ({
      ...previous,
      storeId: selectedStoreId ?? "",
    }));
  }, [selectedStoreId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.storeId ||
      !formData.licenseNumber ||
      !formData.licenseType ||
      !formData.issuingAuthority ||
      !formData.issueDate ||
      !formData.expiryDate
    ) {
      showToast.error("Please fill in all required fields");
      return;
    }

    if (formData.expiryDate <= formData.issueDate) {
      showToast.error("Expiry date must be after the issue date");
      return;
    }

    try {
      setLoading(true);
      await drugLicenseService.create(formData);
      showToast.success("Drug license created successfully");
      navigate("/drug-licenses");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to create drug license",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/drug-licenses")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Drug Licenses
        </button>

        <h1 className="text-2xl font-bold text-gray-900">Add Drug License</h1>
        <p className="text-sm text-gray-500 mt-1">
          Create a new drug license for regulatory compliance
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* License Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            License Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store <span className="text-red-500">*</span>
              </label>
              <select
                name="storeId"
                value={formData.storeId}
                disabled
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a location from the header</option>
                {selectedStoreId && (
                  <option value={selectedStoreId}>
                    {user?.stores?.find((store) => store.id === selectedStoreId)
                      ?.name ?? "Active store"}
                  </option>
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                To use another store, change the active location in the header.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Type <span className="text-red-500">*</span>
              </label>
              <select
                name="licenseType"
                value={formData.licenseType}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LICENSE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., DL/2024/12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issuing Authority <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="issuingAuthority"
                value={formData.issuingAuthority}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., FDA Maharashtra"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Pharmacist Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Registered Pharmacist
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pharmacist Name
              </label>
              <input
                type="text"
                name="registeredPharmacist"
                value={formData.registeredPharmacist}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Number
              </label>
              <input
                type="text"
                name="pharmacistRegNumber"
                value={formData.pharmacistRegNumber}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Information
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Schedules Covered
              </legend>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SCHEDULES.map((schedule) => (
                  <label
                    key={schedule}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={formData.schedulesCovered?.includes(schedule)}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          schedulesCovered: event.target.checked
                            ? [...(previous.schedulesCovered ?? []), schedule]
                            : (previous.schedulesCovered ?? []).filter(
                                (value) => value !== schedule,
                              ),
                        }))
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {schedule.replace("_", " ")}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Required when this licence authorizes controlled or scheduled
                drugs.
              </p>
            </fieldset>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document URL
              </label>
              <input
                type="url"
                name="documentUrl"
                value={formData.documentUrl}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate("/drug-licenses")}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedStoreId}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {loading ? "Creating..." : "Create License"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateDrugLicensePage;
